'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { useFirestore, useAuth } from '@/firebase';
import { collection, doc, setDoc, Timestamp, getDoc, updateDoc, increment } from 'firebase/firestore';
import { Album } from '@/models/album';
import { createVideoMediaItem, parseVideoUrl } from '@/models/media-item';
import {
  uploadGalleryPhotos,
  validateImageFiles,
  type GalleryUploadResponse
} from '@/lib/gallery-upload';
import { getOrCreateMemberPersonalAlbum, getOrCreateFieldFlightsAlbum } from '@/lib/gallery';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/hooks/use-toast';
import { useCollection } from '@/firebase';
import { useUserData } from '@/hooks/use-user-data';
import {
  Upload,
  X,
  Loader2,
  Calendar,
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  FolderOpen,
  User,
  Plane,
  Video,
  Pencil,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import JSZip from 'jszip';
import type { ZipPreviewNode, ZipAlbumMetadata, ZipMediaMetadata } from '@/models/zip-import';

export interface MediaUploadDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
  /** Default album ID to upload to */
  defaultAlbumId?: string;
  /** Initial files dropped onto the gallery */
  initialFiles?: File[];
  /** Callback when upload completes */
  onUploadComplete?: (response: any) => void;
}

type AlbumType = 'field-flights' | 'personal' | 'current' | 'root';
type UploadType = 'photos' | 'video' | 'zip';

/**
 * Enhanced media upload dialog with advanced features:
 * - Photo upload with directory support
 * - Video URL upload (YouTube, Google Drive, etc.)
 * - Field Flights auto-organization
 * - Pill-based tag input
 * - Context-aware title field
 */
export function PhotoUploadDialog({
  open,
  onClose,
  defaultAlbumId,
  initialFiles,
  onUploadComplete,
}: MediaUploadDialogProps) {
  const firestore = useFirestore();
  const auth = useAuth();
  const router = useRouter();
  const { toast } = useToast();

  const [uploadType, setUploadType] = React.useState<UploadType>('photos');
  const [files, setFiles] = React.useState<File[]>([]);
  const [videoUrl, setVideoUrl] = React.useState('');

  // ZIP State
  const [zipFile, setZipFile] = React.useState<File | null>(null);
  const [previewTree, setPreviewTree] = React.useState<ZipPreviewNode | null>(null);
  const [zipStats, setZipStats] = React.useState({ albums: 0, images: 0, size: 0 });
  const [parsingZip, setParsingZip] = React.useState(false);
  const [selectedNode, setSelectedNode] = React.useState<ZipPreviewNode | null>(null);
  const [selectedRootPath, setSelectedRootPath] = React.useState<string>('');
  const [stripRoot, setStripRoot] = React.useState<boolean>(true);
  const [duplicateStrategy, setDuplicateStrategy] = React.useState<'keep-original' | 'overwrite' | 'keep-both'>('keep-original');
  const [albumDuplicateStrategy, setAlbumDuplicateStrategy] = React.useState<'merge' | 'create-new'>('merge');

  const { isAdmin, isOfficer } = useUserData();

  const [albumType, setAlbumType] = React.useState<AlbumType>('field-flights');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [tags, setTags] = React.useState<string[]>([]);
  const [tagInput, setTagInput] = React.useState('');
  const [visibility, setVisibility] = React.useState<'public' | 'members' | 'officers' | 'private'>('public');
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadResponse, setUploadResponse] = React.useState<any>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [fetchingVideoInfo, setFetchingVideoInfo] = React.useState(false);
  const [driveVideos, setDriveVideos] = React.useState<any[]>([]);
  const [selectedDriveVideos, setSelectedDriveVideos] = React.useState<Set<string>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);

  // Common tag suggestions
  const tagSuggestions = [
    'fixed-wing', 'multi-rotor', 'helicopter', 'glider',
    'maiden-flight', 'combat', 'aerobatics', 'racing',
    'fpv', '3d-flying', 'scale', 'warbird',
    'jet', 'trainer', 'vintage', 'scratch-built'
  ];

  // Initialize album type
  React.useEffect(() => {
    if (defaultAlbumId) {
      setAlbumType('current');
    }
  }, [defaultAlbumId]);

  // Reset state when dialog closes or type changes
  React.useEffect(() => {
    if (!open) {
      setFiles([]);
      setVideoUrl('');
      setZipFile(null);
      setPreviewTree(null);
      setSelectedNode(null); // Reset selected node
      setSelectedRootPath('');
      setStripRoot(true);
      setDuplicateStrategy('keep-original');
      setAlbumDuplicateStrategy('merge');
      setAlbumType(defaultAlbumId ? 'current' : 'field-flights');
      setTitle('');
      setDescription('');
      setTags([]);
      setTagInput('');
      setVisibility('public');
      setUploadProgress(0);
      setUploadResponse(null);
      setUploadType('photos');
    } else if (initialFiles && initialFiles.length > 0) {
      // Initialize with dropped files if provided
      setFiles(initialFiles);
      setUploadType('photos');

      // Check for ZIPs in initial files
      const zip = initialFiles.find(f => f.name.toLowerCase().endsWith('.zip'));
      if (zip) {
        setUploadType('zip');
        // We need to call handleZipSelected but we can't do async inside this effect easily
        // and we don't want to trigger it multiple times.
        // For now, just setting type to zip so user sees it.
        // We'll set the zip file for the input but the preview generation needs to happen explicitly
        // or we can invoke it here wrapped.
        handleZipSelected(zip);
      }
    }
  }, [open, defaultAlbumId, initialFiles]);

  // Helper to determine destination path for a file
  const getDestinationPath = React.useCallback(async (file: File) => {
    if (albumType === 'field-flights') {
      // Try to read EXIF for date
      // Note: Full EXIF parsing is heavy, maybe just use modified date as fallback for preview
      // or a lightweight reader if available. 
      // For now, let's use Last Modified date as a quick approximation for the UI
      // The real backend uses EXIF which might differ, but this gives a good hint.
      const date = new Date(file.lastModified);
      const year = date.getFullYear();
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `Field Flights/${year}/${month}/${dateStr}`;
    } else if (albumType === 'personal') {
      return `members/${auth.currentUser?.displayName || 'me'}`;
    } else if (albumType === 'current' && defaultAlbumId) {
      return `Current Album`;
    } else if (albumType === 'root') {
      return title || 'root';
    }
    return '...';
  }, [albumType, defaultAlbumId, auth.currentUser, title]);

  // Helper for ZIP parsing
  const buildPreviewTree = async (zip: JSZip): Promise<{ root: ZipPreviewNode, stats: { albums: number, images: number, size: number } }> => {
    const root: ZipPreviewNode = { name: 'root', path: '', type: 'folder', children: [], albumCount: 0, imageCount: 0 };
    const nodeMap = new Map<string, ZipPreviewNode>();
    nodeMap.set('', root);

    let albums = 0;
    let images = 0;
    let size = 0;

    // 1. Build Folders & Files
    // Get all entries and sort by path length to ensure parents exist before children
    const entries = Object.keys(zip.files).sort();

    for (const path of entries) {
      if (path.startsWith('__MACOSX') || path.includes('/.')) continue;

      const zipEntry = zip.files[path];
      const isDir = zipEntry.dir;
      const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;
      const parts = cleanPath.split('/');
      const name = parts[parts.length - 1];
      const parentPath = parts.slice(0, -1).join('/');

      let parent = nodeMap.get(parentPath);
      if (!parent) {
        // Should ideally not happen if sorted, but we can creating missing parents if needed or just attach to root fallback
        // For now assume root if top level
        if (parts.length === 1) parent = root;
        else continue; // Orphan or skipped parent
      }

      if (isDir) {
        const node: ZipPreviewNode = {
          name,
          path: cleanPath,
          type: 'folder',
          children: [],
          albumCount: 0,
          imageCount: 0
        };
        nodeMap.set(cleanPath, node);
        parent.children = parent.children || [];
        parent.children.push(node);
        albums++;
      } else {
        // File
        // Check extension
        const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(name);
        const isMeta = name.endsWith('.meta') || name.endsWith('.json');

        if (isImage) {
          images++;
          // @ts-ignore
          size += zipEntry._data ? zipEntry._data.uncompressedSize : 0;
          parent.imageCount = (parent.imageCount || 0) + 1;

          const node: ZipPreviewNode = {
            name,
            path: cleanPath,
            type: 'file',
            // @ts-ignore
            size: zipEntry._data?.uncompressedSize
          };
          parent.children = parent.children || [];
          parent.children.push(node);
          nodeMap.set(cleanPath, node); // Map image too so we can attach meta later
        } else if (isMeta) {
          // Load content immediately for preview
          try {
            const content = await zipEntry.async('string');
            const meta = JSON.parse(content);

            // Determine target
            // If album.meta -> target is the parent folder
            if (name === 'album.meta' || name === 'album.meta.json') {
              parent.meta = meta;
              // Update parent displayName if title/name exists
              if (meta.title || meta.name) {
                // We store the original name but maybe we want a displayName field?
                // Or just override name for preview? 
                // Let's add a displayName field to ZipPreviewNode
                (parent as any).displayName = meta.title || meta.name;
              }
            } else {
              // Image meta: image.jpg.meta
              const targetName = name.replace(/\.meta(.json)?$/, '');
              const targetPath = parentPath ? `${parentPath}/${targetName}` : targetName;
              const targetNode = nodeMap.get(targetPath);

              if (targetNode) {
                targetNode.meta = meta;
                if (meta.title || meta.name) {
                  (targetNode as any).displayName = meta.title || meta.name;
                }
              }
            }
          } catch (e) {
            console.warn("Error parsing meta", path);
          }
        }
      }
    }

    // Recursively update counts (bottom-up would be better but simple recursion works for small trees)
    const updateCounts = (node: ZipPreviewNode) => {
      let aCount = 0;
      let iCount = 0;
      if (node.children) {
        node.children.forEach(child => {
          if (child.type === 'folder') {
            updateCounts(child);
            aCount += 1 + (child.albumCount || 0);
            iCount += (child.imageCount || 0);
          } else {
            // Image
          }
        });
        node.albumCount = aCount;
        node.imageCount = (node.imageCount || 0) + iCount;
      }
    };
    updateCounts(root);

    return { root, stats: { albums, images, size } };
  };

  // Collect all folder paths from preview tree for root selection dropdown
  const collectFolderPaths = React.useCallback((node: ZipPreviewNode, paths: { path: string; label: string; depth: number }[] = [], depth = 0): { path: string; label: string; depth: number }[] => {
    if (node.type === 'folder') {
      const displayName = (node as any).displayName || node.name;
      paths.push({
        path: node.path,
        label: displayName,
        depth
      });
      node.children?.forEach(child => collectFolderPaths(child, paths, depth + 1));
    }
    return paths;
  }, []);

  const folderOptions = React.useMemo(() => {
    if (!previewTree) return [];
    return collectFolderPaths(previewTree);
  }, [previewTree, collectFolderPaths]);

  const handleZipSelected = async (file: File) => {
    setZipFile(file);
    setParsingZip(true);
    try {
      const zip = await JSZip.loadAsync(file);
      const tree = await buildPreviewTree(zip);
      setPreviewTree(tree.root);
      setZipStats(tree.stats);
    } catch (err) {
      console.error("Failed to parse zip", err);
      toast({
        title: "Error parsing ZIP",
        description: "Could not read the ZIP file structure.",
        variant: "destructive"
      });
      setZipFile(null);
    } finally {
      setParsingZip(false);
    }
  };



  // Handle file selection - appends new files to existing ones
  const handleFileChange = async (newFiles: File[]) => {
    // Validate files
    const validation = validateImageFiles(newFiles);
    if (!validation.valid) {
      toast({
        variant: 'destructive',
        title: 'Invalid files',
        description: validation.errors.map(e => `${e.filename}: ${e.error}`).join('\n'),
      });
      return;
    }

    // Merge with existing files, avoiding duplicates by name
    setFiles(prevFiles => {
      const existingNames = new Set(prevFiles.map(f => f.name));
      const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
      return [...prevFiles, ...uniqueNewFiles];
    });

    // Count unique new files
    const existingNames = new Set(files.map(f => f.name));
    const uniqueCount = newFiles.filter(f => !existingNames.has(f.name)).length;
    const totalCount = files.length + uniqueCount;

    toast({
      title: 'Files added',
      description: uniqueCount > 0
        ? `Added ${uniqueCount} file(s) • Total: ${totalCount}`
        : 'No new files (duplicates skipped)',
      duration: 3000,
    });
  };

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );

    if (droppedFiles.length > 0) {
      handleFileChange(droppedFiles);
    }
  }, []);

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileChange(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const validateVideoUrl = (url: string): boolean => {
    if (!url.trim()) return false;

    // Check for supported video platforms
    const supportedPatterns = [
      /youtube\.com\/watch\?v=/,
      /youtu\.be\//,
      /youtube\.com\/shorts\//,
      /vimeo\.com\/\d+/,
      // Google Drive patterns - all formats
      /drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+/,  // Matches /view, /preview, etc.
      /drive\.google\.com\/open\?id=/,
      /drive\.google\.com\/uc\?id=/,
      /drive\.google\.com\/drive\/folders\//,  // Google Drive folder
    ];

    return supportedPatterns.some(pattern => pattern.test(url));
  };

  const isDriveFolderUrl = (url: string): boolean => {
    return /drive\.google\.com\/drive\/folders\//.test(url);
  };

  const fetchDriveFolderVideos = async (url: string) => {
    setFetchingVideoInfo(true);
    setDriveVideos([]);
    setSelectedDriveVideos(new Set());

    try {
      const response = await fetch(`/api/google-drive?url=${encodeURIComponent(url)}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch folder videos');
      }

      const data = await response.json();

      if (data.type === 'folder' && data.videos) {
        setDriveVideos(data.videos);
        // Auto-select all videos
        setSelectedDriveVideos(new Set(data.videos.map((v: any) => v.id)));

        toast({
          title: 'Folder loaded',
          description: `Found ${data.videos.length} video(s) in folder`,
        });
      }
    } catch (error) {
      console.error('Error fetching Drive folder:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load folder',
        description: error instanceof Error ? error.message : 'Could not fetch videos from Google Drive folder',
      });
    } finally {
      setFetchingVideoInfo(false);
    }
  };

  const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const fetchYouTubeMetadata = async (url: string) => {
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return;

    setFetchingVideoInfo(true);
    try {
      // Use noembed.com API which doesn't require API key
      const response = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.title) {
          setTitle(data.title);
        }
        if (data.author_name) {
          setDescription(`Video by ${data.author_name}`);
        }
        // No toast - the loading spinner provides enough feedback
      }
    } catch (error) {
      console.error('Error fetching YouTube metadata:', error);
      // Silently fail - user can still enter title manually
    } finally {
      setFetchingVideoInfo(false);
    }
  };

  // Auto-fetch metadata when URL changes
  React.useEffect(() => {
    if (uploadType === 'video' && videoUrl) {
      const isYouTube = /youtube\.com|youtu\.be/.test(videoUrl);
      const isDriveFolder = isDriveFolderUrl(videoUrl);

      if (validateVideoUrl(videoUrl)) {
        // Debounce to avoid fetching on every keystroke
        const timer = setTimeout(() => {
          if (isDriveFolder) {
            fetchDriveFolderVideos(videoUrl);
          } else if (isYouTube) {
            fetchYouTubeMetadata(videoUrl);
          }
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [videoUrl, uploadType]);

  const handleBatchDriveUpload = async () => {
    const user = auth.currentUser;
    if (!user || !firestore) {
      toast({
        variant: 'destructive',
        title: 'Authentication required',
        description: 'Please sign in to upload videos',
      });
      return;
    }

    const selectedVideos = driveVideos.filter(v => selectedDriveVideos.has(v.id));
    if (selectedVideos.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No videos selected',
        description: 'Please select at least one video to import',
      });
      return;
    }

    // Determine target album
    let targetAlbumId = '';
    try {
      if (albumType === 'field-flights') {
        const today = new Date().toISOString().split('T')[0];
        const fieldFlightsAlbum = await getOrCreateFieldFlightsAlbum(firestore, today, user.uid);
        targetAlbumId = fieldFlightsAlbum.id;
      } else if (albumType === 'personal') {
        const personalAlbum = await getOrCreateMemberPersonalAlbum(firestore, user.uid, user.displayName || 'Member');
        targetAlbumId = personalAlbum.id;
      } else if (albumType === 'current' && defaultAlbumId) {
        targetAlbumId = defaultAlbumId;
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error creating album',
        description: error instanceof Error ? error.message : 'Failed to create album',
      });
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const albumRef = doc(firestore, 'albums', targetAlbumId);
      const albumSnap = await getDoc(albumRef);
      const albumPath = albumSnap.data()?.albumPath || '';

      // Fetch user profile for name
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();
      const uploaderName = userData?.displayName ||
        (userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
        user.displayName ||
        user.email ||
        'Member';

      // Upload each selected video
      for (const video of selectedVideos) {
        try {
          const mediaId = doc(collection(firestore, 'media')).id;
          const videoItem = createVideoMediaItem({
            title: video.name,
            description: description || undefined,
            videoUrl: video.webViewLink || `https://drive.google.com/file/d/${video.id}/view`,
            albumId: targetAlbumId,
            albumPath,
            tags,
            uploadedBy: user.uid,
            uploaderName,
            visibility,
            thumbnailUrl: video.thumbnailUrl,
            width: video.width,
            height: video.height,
            duration: video.duration,
          });

          const cleanVideoItem = Object.fromEntries(
            Object.entries({ ...videoItem, id: mediaId }).filter(([_, value]) => value !== undefined)
          );

          await setDoc(doc(firestore, 'media', mediaId), cleanVideoItem);
          successCount++;
        } catch (error) {
          console.error(`Error uploading video ${video.name}:`, error);
          errorCount++;
        }
      }

      // Update album media count
      await updateDoc(albumRef, {
        mediaCount: increment(successCount),
        lastMediaAddedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Import complete',
        description: `Successfully imported ${successCount} video(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        duration: 5000,
        action: (
          <ToastAction
            altText="View album"
            onClick={() => router.push(`/gallery/${targetAlbumId}`)}
          >
            View Album
          </ToastAction>
        ),
      });

      if (onUploadComplete) {
        onUploadComplete({ success: true, count: successCount });
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Batch upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Authentication required',
        description: 'Please sign in to upload videos',
      });
      return;
    }

    if (!firestore) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Firestore not initialized',
      });
      return;
    }

    if (!videoUrl.trim()) {
      toast({
        variant: 'destructive',
        title: 'No video URL provided',
        description: 'Please enter a YouTube, Vimeo, or Google Drive video URL',
      });
      return;
    }

    if (!validateVideoUrl(videoUrl)) {
      toast({
        variant: 'destructive',
        title: 'Invalid video URL',
        description: 'Please enter a valid YouTube, Vimeo, or Google Drive video URL',
      });
      return;
    }

    let targetAlbumId = '';

    // Determine target album based on selection
    try {
      if (albumType === 'field-flights') {
        const today = new Date().toISOString().split('T')[0];
        const fieldFlightsAlbum = await getOrCreateFieldFlightsAlbum(
          firestore,
          today,
          user.uid
        );
        targetAlbumId = fieldFlightsAlbum.id;
      } else if (albumType === 'personal') {
        const personalAlbum = await getOrCreateMemberPersonalAlbum(
          firestore,
          user.uid,
          user.displayName || 'Member'
        );
        targetAlbumId = personalAlbum.id;
      } else if (albumType === 'current' && defaultAlbumId) {
        // Check if album exists in Firestore
        const albumRef = doc(firestore, 'albums', defaultAlbumId);
        const albumSnap = await getDoc(albumRef);

        if (albumSnap.exists()) {
          targetAlbumId = defaultAlbumId;
        } else {
          throw new Error('Target album does not exist.');
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error preparing album',
        description: error instanceof Error ? error.message : 'Failed to prepare target album',
      });
      return;
    }

    setUploading(true);

    try {
      // Get album data
      const albumRef = doc(firestore, 'albums', targetAlbumId);
      const albumSnap = await getDoc(albumRef);
      const albumData = albumSnap.data();
      const albumPath = albumData?.albumPath || '';

      // Parse video URL to get provider info
      const videoInfo = parseVideoUrl(videoUrl);

      // Fetch user profile for name
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();
      const uploaderName = userData?.displayName ||
        (userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
        user.displayName ||
        user.email ||
        'Member';

      // Create video media item
      const mediaId = doc(collection(firestore, 'media')).id;
      const videoItem = createVideoMediaItem({
        title: title || 'Video',
        description: description || undefined,
        videoUrl: videoUrl,
        albumId: targetAlbumId,
        albumPath,
        tags,
        uploadedBy: user.uid,
        uploaderName,
        visibility,
      });

      // Remove undefined fields (Firestore doesn't accept them)
      const cleanVideoItem = Object.fromEntries(
        Object.entries({ ...videoItem, id: mediaId }).filter(([_, value]) => value !== undefined)
      );

      // Save to Firestore
      await setDoc(doc(firestore, 'media', mediaId), cleanVideoItem);

      // Update album media count
      await updateDoc(albumRef, {
        mediaCount: increment(1),
        lastMediaAddedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Video added successfully',
        description: 'Your video has been added to the gallery',
        duration: 5000,
        action: (
          <ToastAction
            altText="View album"
            onClick={() => router.push(`/gallery/${targetAlbumId}`)}
          >
            View Album
          </ToastAction>
        ),
      });

      if (onUploadComplete) {
        onUploadComplete({
          success: true,
          uploadedMedia: [{ id: mediaId, videoUrl, title }],
        });
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Video upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoUpload = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Authentication required',
        description: 'Please sign in to upload photos',
      });
      return;
    }

    if (!firestore) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Firestore not initialized',
      });
      return;
    }

    if (files.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No files selected',
        description: 'Please select at least one photo to upload',
      });
      return;
    }

    let targetAlbumId = '';

    // Determine target album based on selection
    try {
      if (albumType === 'field-flights') {
        const today = new Date().toISOString().split('T')[0];
        const fieldFlightsAlbum = await getOrCreateFieldFlightsAlbum(
          firestore,
          today,
          user.uid
        );
        targetAlbumId = fieldFlightsAlbum.id;
      } else if (albumType === 'personal') {
        const personalAlbum = await getOrCreateMemberPersonalAlbum(
          firestore,
          user.uid,
          user.displayName || 'Member'
        );
        targetAlbumId = personalAlbum.id;
      } else if (albumType === 'current' && defaultAlbumId) {
        // Check if album exists in Firestore
        const albumRef = doc(firestore, 'albums', defaultAlbumId);
        const albumSnap = await getDoc(albumRef);

        if (albumSnap.exists()) {
          targetAlbumId = defaultAlbumId;
        } else {
          throw new Error('Target album does not exist.');
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error creating album',
        description: error instanceof Error ? error.message : 'Failed to create album',
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await uploadGalleryPhotos(files, {
        albumId: targetAlbumId,
        title,
        description,
        tags,
        visibility,
        useExifDate: albumType === 'field-flights',
        duplicateStrategy,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      setUploadResponse(response);

      const successCount = response.uploadedMedia?.length || 0;
      const errorCount = response.errors?.length || 0;
      const dupSkipped = response.duplicatesSkipped || 0;
      const dupOverwritten = response.duplicatesOverwritten || 0;

      if (successCount > 0 || dupSkipped > 0) {
        const parts = [`Successfully uploaded ${successCount} photo(s)`];
        if (dupSkipped > 0) parts.push(`${dupSkipped} duplicate(s) skipped`);
        if (dupOverwritten > 0) parts.push(`${dupOverwritten} duplicate(s) overwritten`);

        toast({
          title: 'Upload complete',
          description: parts.join('. '),
          duration: 5000,
          action: (
            <ToastAction
              altText="View album"
              onClick={() => router.push(`/gallery/${targetAlbumId}`)}
            >
              View Album
            </ToastAction>
          ),
        });

        if (onUploadComplete) {
          onUploadComplete(response);
        }

        setTimeout(() => {
          onClose();
        }, 1500);
      }

      if (errorCount > 0) {
        toast({
          variant: 'destructive',
          title: 'Some uploads failed',
          description: `${errorCount} file(s) failed to upload`,
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleZipUpload = async () => {
    if (!zipFile) return;

    const user = auth.currentUser;
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Authentication required',
        description: 'Please sign in to upload ZIP files',
      });
      return;
    }

    setUploading(true);
    try {
      // Re-read file if we modified metadata? 
      // Actually, we modified the PREVIEW tree metadata. 
      // To persist this, we should send the modified metadata structure alongside the file.
      // But the backend reads from the ZIP 'album.meta'/'image.meta'. 
      // If we want "Interactive Editing" to work, we must either:
      // A) Re-write the ZIP file on client (complex/expensive).
      // B) Send a "metadata overrides" JSON payload to the server.

      // For now, let's assume the user edits are visual only OR we implement (B) quickly.
      // The user asked "We should get previews... (editable) ...". 
      // So editing must have effect. 
      // I will extract the metadata from the previewTree and send it as 'overrides'.

      // Extract overrides from tree
      const overrides: Record<string, any> = {};
      const collectOverrides = (node: ZipPreviewNode) => {
        if (node.meta) {
          // Key by path relative to zip root
          overrides[node.path] = node.meta;
        }
        node.children?.forEach(collectOverrides);
      };
      if (previewTree) collectOverrides(previewTree);

      const formData = new FormData();
      formData.append('file', zipFile);
      formData.append('albumType', albumType);
      if (defaultAlbumId) formData.append('defaultAlbumId', defaultAlbumId);
      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      if (tags.length > 0) formData.append('tags', tags.join(','));

      // Root selection options
      formData.append('zipRootPath', selectedRootPath);
      formData.append('stripRoot', String(stripRoot));
      formData.append('duplicateStrategy', duplicateStrategy);
      formData.append('albumDuplicateStrategy', albumDuplicateStrategy);

      // Send overrides
      formData.append('metadataOverrides', JSON.stringify(overrides));

      const token = await user.getIdToken();
      const res = await fetch('/api/gallery/import-zip', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Upload failed');
      }

      const data = await res.json();

      const parts = [];
      if (data.albumsCreated > 0 || data.albumsMerged > 0) {
        const albumParts = [];
        if (data.albumsCreated > 0) albumParts.push(`${data.albumsCreated} created`);
        if (data.albumsMerged > 0) albumParts.push(`${data.albumsMerged} merged`);
        parts.push(`Albums: ${albumParts.join(', ')}`);
      }
      parts.push(`${data.mediaCreated} images imported`);
      if (data.duplicatesSkipped > 0) {
        parts.push(`${data.duplicatesSkipped} duplicate(s) skipped`);
      }
      if (data.duplicatesOverwritten > 0) {
        parts.push(`${data.duplicatesOverwritten} duplicate(s) overwritten`);
      }

      toast({
        title: "Import Successful",
        description: parts.join('. ') + '.',
        action: (
          <ToastAction
            altText="Reload"
            onClick={() => window.location.reload()} // Simple reload to show new structure
          >
            Reload Gallery
          </ToastAction>
        ),
      });

      if (onUploadComplete) onUploadComplete(data);
      onClose();

    } catch (error: any) {
      console.error("ZIP Import failed", error);
      toast({
        variant: "destructive",
        title: "Import Failed",
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (uploadType === 'zip') {
      return handleZipUpload();
    } else if (uploadType === 'video') {
      // If we have Drive videos loaded, do batch upload
      if (driveVideos.length > 0) {
        return handleBatchDriveUpload();
      }
      return handleVideoUpload();
    } else {
      return handlePhotoUpload();
    }
  };

  const canUpload = uploadType === 'photos'
    ? files.length > 0 && !uploading
    : uploadType === 'zip'
      ? zipFile !== null && !uploading
      : driveVideos.length > 0
        ? selectedDriveVideos.size > 0 && !uploading
        : videoUrl.trim() !== '' && !uploading;
  const filteredSuggestions = tagSuggestions
    .filter(s => !tags.includes(s) && s.toLowerCase().includes(tagInput.toLowerCase()))
    .slice(0, 8);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl lg:max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Media</DialogTitle>
          <DialogDescription>
            Upload photos, video URLs, or import ZIP albums.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={uploadType} onValueChange={(v) => setUploadType(v as UploadType)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="photos">
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos
            </TabsTrigger>
            <TabsTrigger value="video">
              <Video className="w-4 h-4 mr-2" />
              Video URL
            </TabsTrigger>
            <TabsTrigger value="zip">
              <FolderOpen className="w-4 h-4 mr-2" />
              ZIP Import
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos" className="space-y-4 py-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              <input
                id="file-upload"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    handleFileChange(Array.from(e.target.files));
                  }
                }}
              />
              <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground">
                Drag and drop photos here, or click to select
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Supports JPG, PNG, WebP
              </p>
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between px-2">
                  <h3 className="font-semibold text-sm">{files.length} file(s) selected</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-destructive hover:text-destructive"
                    onClick={() => setFiles([])}
                  >
                    Clear all
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-4 px-4 py-2 text-xs font-semibold text-muted-foreground border-b">
                  <div>File Name</div>
                  <div>Destination (Est.)</div>
                  <div className="text-right">Size</div>
                  <div className="w-8"></div>
                </div>
                <div className="max-h-[150px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                  {files.map((file, index) => (
                    <FileListItem
                      key={`${file.name}-${index}`}
                      file={file}
                      getDestinationPath={getDestinationPath}
                      onRemove={() => {
                        setFiles(files.filter((_, i) => i !== index));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Duplicate Handling for Photos */}
            <div className="flex flex-col sm:flex-row gap-4 p-3 bg-muted/20 rounded-md border">
              <div className="flex-1">
                <Label className="text-xs mb-1.5 block">If Duplicate Found</Label>
                <Select value={duplicateStrategy} onValueChange={(v) => setDuplicateStrategy(v as typeof duplicateStrategy)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep-original">
                      <span className="font-medium">Keep original</span>
                      <span className="text-muted-foreground ml-2 text-xs">(skip duplicates)</span>
                    </SelectItem>
                    <SelectItem value="overwrite">
                      <span className="font-medium">Overwrite</span>
                      <span className="text-muted-foreground ml-2 text-xs">(replace existing)</span>
                    </SelectItem>
                    <SelectItem value="keep-both">
                      <span className="font-medium">Keep both</span>
                      <span className="text-muted-foreground ml-2 text-xs">(rename new file)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end text-xs text-muted-foreground">
                Duplicates detected by file content (CRC32)
              </div>
            </div>
          </TabsContent>

          <TabsContent value="video" className="space-y-4 py-4">
            <div>
              <Label htmlFor="videoUrl">Video URL</Label>
              <div className="flex gap-2">
                <Input
                  id="videoUrl"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/..."
                  disabled={fetchingVideoInfo}
                />
              </div>
              {fetchingVideoInfo && (
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> Fetching video details...
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Supports YouTube, Vimeo, Google Drive, and more
              </p>
            </div>
          </TabsContent>

          <TabsContent value="zip" className="space-y-4 py-4">
            {!zipFile ? (
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => document.getElementById('zip-upload')?.click()}
              >
                <input
                  id="zip-upload"
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleZipSelected(e.target.files[0]);
                  }}
                />
                <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p>Click to select a ZIP file</p>
                <p className="text-xs text-muted-foreground">Import folder structures and metadata</p>
              </div>
            ) : (
              <div className="border rounded-md p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 font-semibold">
                    <span className="bg-primary/10 p-2 rounded"><FolderOpen className="w-4 h-4 text-primary" /></span>
                    {zipFile.name}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setZipFile(null); setPreviewTree(null); }}>Change</Button>
                </div>

                {parsingZip && <div className="flex items-center gap-2 py-8 justify-center"><Loader2 className="animate-spin" /> Parsing ZIP structure...</div>}

                {!parsingZip && previewTree && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-center text-sm bg-muted/30 p-2 rounded">
                      <div>
                        <p className="font-bold">{zipStats.albums}</p>
                        <p className="text-muted-foreground text-xs">Albums</p>
                      </div>
                      <div>
                        <p className="font-bold">{zipStats.images}</p>
                        <p className="text-muted-foreground text-xs">Images</p>
                      </div>
                      <div>
                        <p className="font-bold">{(zipStats.size / 1024 / 1024).toFixed(1)} MB</p>
                        <p className="text-muted-foreground text-xs">Total Size</p>
                      </div>
                    </div>

                    {/* Root Selection Controls */}
                    <div className="flex flex-col sm:flex-row gap-4 p-3 bg-muted/20 rounded-md border">
                      <div className="flex-1">
                        <Label className="text-xs mb-1.5 block">Import From</Label>
                        <Select
                          value={selectedRootPath || '__zip_root__'}
                          onValueChange={(v) => setSelectedRootPath(v === '__zip_root__' ? '' : v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="ZIP Root (import everything)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__zip_root__">
                              <span className="font-medium">ZIP Root</span>
                              <span className="text-muted-foreground ml-2 text-xs">(import everything)</span>
                            </SelectItem>
                            {folderOptions.filter(f => f.path !== '').map(folder => (
                              <SelectItem key={folder.path} value={folder.path}>
                                <span style={{ paddingLeft: (folder.depth - 1) * 12 }}>
                                  {folder.label}
                                </span>
                                <span className="text-muted-foreground ml-2 text-xs">/{folder.path}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end gap-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="strip-root"
                            checked={!stripRoot}
                            onCheckedChange={(checked) => setStripRoot(!checked)}
                          />
                          <Label htmlFor="strip-root" className="text-sm cursor-pointer">
                            Include root folder in path
                          </Label>
                        </div>
                      </div>
                    </div>

                    {/* Duplicate Handling */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-muted/20 rounded-md border">
                      <div>
                        <Label className="text-xs mb-1.5 block">Album Duplicates</Label>
                        <Select value={albumDuplicateStrategy} onValueChange={(v) => setAlbumDuplicateStrategy(v as typeof albumDuplicateStrategy)}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="merge">
                              <span className="font-medium">Merge</span>
                              <span className="text-muted-foreground ml-2 text-xs">(add to existing)</span>
                            </SelectItem>
                            <SelectItem value="create-new">
                              <span className="font-medium">Create new</span>
                              <span className="text-muted-foreground ml-2 text-xs">(separate album)</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Albums matched by name</p>
                      </div>
                      <div>
                        <Label className="text-xs mb-1.5 block">Image Duplicates</Label>
                        <Select value={duplicateStrategy} onValueChange={(v) => setDuplicateStrategy(v as typeof duplicateStrategy)}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="keep-original">
                              <span className="font-medium">Keep original</span>
                              <span className="text-muted-foreground ml-2 text-xs">(skip)</span>
                            </SelectItem>
                            <SelectItem value="overwrite">
                              <span className="font-medium">Overwrite</span>
                              <span className="text-muted-foreground ml-2 text-xs">(replace)</span>
                            </SelectItem>
                            <SelectItem value="keep-both">
                              <span className="font-medium">Keep both</span>
                              <span className="text-muted-foreground ml-2 text-xs">(rename)</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Images matched by content (CRC32)</p>
                      </div>
                    </div>
                    {selectedRootPath && (
                      <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 p-2 rounded border border-blue-200 dark:border-blue-800">
                        <span className="font-medium">Preview:</span> Importing from <code className="bg-muted px-1 rounded">{selectedRootPath}</code>
                        {stripRoot
                          ? <> — contents will be placed directly in target album</>
                          : <> — folder <code className="bg-muted px-1 rounded">{selectedRootPath.split('/').pop()}</code> will be created in target album</>
                        }
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-[300px]">
                      {/* Left: Tree */}
                      <div className="border rounded-md p-2 overflow-y-auto bg-background custom-scrollbar text-sm">
                        <ZipPreviewTree
                          node={previewTree}
                          onSelect={(n) => setSelectedNode(n)}
                          selectedPath={selectedNode?.path}
                        />
                      </div>

                      {/* Right: Inspector */}
                      <div className="border rounded-md p-4 bg-muted/10 overflow-y-auto text-sm">
                        {!selectedNode ? (
                          <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                            <Info className="w-8 h-8 mb-2 opacity-50" />
                            <p>Select a folder or file to view details</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-start gap-2 border-b pb-2">
                              {selectedNode.type === 'folder' ? <FolderOpen className="w-5 h-5 text-blue-500 mt-0.5" /> : <ImageIcon className="w-5 h-5 text-green-500 mt-0.5" />}
                              <div className="break-all">
                                <p className="font-semibold">{selectedNode.name}</p>
                                <p className="text-xs text-muted-foreground">{selectedNode.path || "/"}</p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Display Name (Title)</Label>
                              <div className="flex gap-2">
                                <Input
                                  className="h-8"
                                  value={(selectedNode as any).displayName || (selectedNode.meta as any)?.title || selectedNode.meta?.name || selectedNode.name}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    selectedNode.meta = selectedNode.meta || { name: selectedNode.name };
                                    selectedNode.meta.title = val;
                                    (selectedNode as any).displayName = val;
                                    setZipStats({ ...zipStats }); // Force render
                                  }}
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Description</Label>
                              <Textarea
                                className="h-20 text-xs resize-none"
                                value={selectedNode.meta?.description || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  selectedNode.meta = selectedNode.meta || { name: selectedNode.name };
                                  selectedNode.meta.description = val;
                                  setZipStats({ ...zipStats }); // Force render
                                }}
                                placeholder="No description"
                              />
                            </div>

                            {selectedNode.type === 'file' && (
                              <div className="space-y-2">
                                <Label className="text-xs">Tags (Comma separated)</Label>
                                <Input
                                  className="h-8"
                                  value={selectedNode.meta?.tags?.join(', ') || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    selectedNode.meta = selectedNode.meta || { name: selectedNode.name };
                                    selectedNode.meta.tags = val.split(',').map(s => s.trim()).filter(Boolean);
                                    setZipStats({ ...zipStats }); // Force render
                                  }}
                                />
                              </div>
                            )}
                            {selectedNode.meta && (
                              <div className="pt-2">
                                <p className="text-xs text-muted-foreground mb-1">Raw Metadata:</p>
                                <pre className="text-[10px] bg-muted p-2 rounded overflow-x-auto">
                                  {JSON.stringify(selectedNode.meta, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* SHARED CONTROLS */}
          <div className="space-y-4 pt-4 border-t">
            <Label className="mb-2 block">Organization</Label>
            <RadioGroup value={albumType} onValueChange={(v) => setAlbumType(v as AlbumType)} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary/5">
                <RadioGroupItem value="field-flights" id="ff" />
                <Label htmlFor="ff" className="cursor-pointer flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    <Plane className="w-4 h-4" />
                    Field Flight
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Organize by date
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary/5">
                <RadioGroupItem value="personal" id="personal" />
                <Label htmlFor="personal" className="cursor-pointer flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Personal
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Your member album
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary/5">
                <RadioGroupItem value="current" id="current" disabled={!defaultAlbumId && albumType !== 'root'} />
                <Label htmlFor="current" className={cn("cursor-pointer flex-1", (!defaultAlbumId && albumType !== 'root') && "opacity-50")}>
                  <div className="font-semibold flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Current
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    This album only
                  </div>
                </Label>
              </div>
              {(isAdmin || isOfficer) && (
                <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 transition-colors [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary/5">
                  <RadioGroupItem value="root" id="root" />
                  <Label htmlFor="root" className="cursor-pointer flex-1">
                    <div className="font-semibold flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-red-500" />
                      Root
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Top level (Admin)
                    </div>
                  </Label>
                </div>
              )}
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={uploadType === 'zip' ? "Folder Name (Root)" : "e.g. Flight Day"}
              />
            </div>
            <div>
              <Label htmlFor="tags">Tags</Label>
              <div className="relative">
                <Input
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag(tagInput);
                    }
                  }}
                  placeholder="Press Enter to add tags"
                />
              </div>
              {tagInput && filteredSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md p-1">
                  {filteredSuggestions.map(suggestion => (
                    <button
                      key={suggestion}
                      className="w-full text-left px-2 py-1 text-sm rounded hover:bg-accent hover:text-accent-foreground"
                      onClick={() => addTag(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="pl-2 pr-1 py-0.5 h-6">
                    {tag}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 ml-1 hover:bg-transparent hover:text-destructive"
                      onClick={() => removeTag(i)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details..."
              className="h-20"
            />
          </div>

        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={
            uploading ||
            (uploadType === 'photos' && files.length === 0) ||
            (uploadType === 'video' && !videoUrl) ||
            (uploadType === 'zip' && !zipFile)
          }>
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {uploadType === 'zip' ? 'Importing...' : 'Uploading...'}
              </>
            ) : (
              <>
                {uploadType === 'zip' ? <FolderOpen className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
                {uploadType === 'zip' ? 'Import ZIP' : uploadType === 'photos' ? `Upload ${files.length} Photo(s)` : 'Upload Video'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

}

// ----------------------------------------------------------------------
// Zip Upload Sub-Component
// ----------------------------------------------------------------------



async function buildPreviewTree(zip: JSZip): Promise<{ root: ZipPreviewNode, stats: { albums: number, images: number, size: number } }> {
  const root: ZipPreviewNode = { path: '', name: 'Root', type: 'folder', children: [] };
  const stats = { albums: 0, images: 0, size: 0 };
  const nodeMap = new Map<string, ZipPreviewNode>();
  nodeMap.set('', root);

  // Filter out __MACOSX and hidden files
  const entries = Object.entries(zip.files).filter(([path]) => !path.startsWith('__MACOSX') && !path.includes('/.') && !path.startsWith('.'));

  // 1. Build Folders
  for (const [path, file] of entries) {
    if (file.dir) {
      const parts = path.split('/').filter(Boolean);
      let currentPath = '';
      let parent = root;

      for (const part of parts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!nodeMap.has(currentPath)) {
          const newNode: ZipPreviewNode = {
            path: currentPath,
            name: part,
            type: 'folder',
            children: [],
            albumCount: 0,
            imageCount: 0
          };
          nodeMap.set(currentPath, newNode);
          parent.children = parent.children || [];
          parent.children.push(newNode);
          stats.albums++;
        }
        parent = nodeMap.get(currentPath)!;
      }
    }
  }

  // 2. Place Files and Metadata
  for (const [path, file] of entries) {
    if (!file.dir) {
      const parts = path.split('/');
      const fileName = parts.pop()!;
      const parentPath = parts.join('/');

      let parentPathStr = parentPath || '';
      let parent = nodeMap.get(parentPathStr);

      if (!parent && parentPathStr) {
        parent = root; // Fallback
      } else if (!parent) {
        parent = root;
      }

      const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName);
      const isMeta = fileName.endsWith('.meta') || fileName.endsWith('.json');

      if (isImage) {
        stats.images++;
        // @ts-ignore - _data is internal but standard in JSZip
        stats.size += (file as any)._data ? (file as any)._data.uncompressedSize : 0;
        if (parent) parent.imageCount = (parent.imageCount || 0) + 1;

        parent.children = parent.children || [];
        parent.children.push({
          path,
          name: fileName,
          type: 'file',
          // @ts-ignore
          size: (file as any)._data?.uncompressedSize
        });
      } else if (isMeta) {
        try {
          const content = await file.async('string');
          const meta = JSON.parse(content);

          if (fileName === 'album.meta' || fileName === 'album.meta.json') {
            parent.meta = meta;
          } else {
            const imageNode = parent.children?.find(c => c.name + '.meta' === fileName || c.name + '.json' === fileName || c.name + '.jpg.meta' === fileName);
            if (!imageNode && fileName.endsWith('.meta')) {
              const targetName = fileName.replace('.meta', '');
              const target = parent.children?.find(c => c.name === targetName);
              if (target) target.meta = meta;
            } else if (imageNode) {
              imageNode.meta = meta;
            }
          }
        } catch (e) { console.warn("Failed to parse meta", fileName) }
      }
    }
  }

  return { root, stats };
}

function ZipPreviewTree({
  node,
  depth = 0,
  onSelect,
  selectedPath
}: {
  node: ZipPreviewNode,
  depth?: number,
  onSelect?: (node: ZipPreviewNode) => void,
  selectedPath?: string
}) {
  const displayName = (node as any).displayName || node.name;
  const isSelected = selectedPath === node.path;

  return (
    <div key={node.path} style={{ marginLeft: depth * 12 }} className="text-sm">
      <div
        className={cn(
          "flex items-center gap-2 py-1 px-2 rounded-sm cursor-pointer transition-colors group",
          isSelected ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(node);
        }}
      >
        {node.type === 'folder' ? <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <ImageIcon className="w-4 h-4 text-green-500 flex-shrink-0" />}
        <span className={cn(node.type === 'folder' && "font-semibold", "truncate")}>
          {displayName}
          {displayName !== node.name && <span className="text-xs text-muted-foreground ml-2 font-normal">({node.name})</span>}
        </span>

        {/* Info / Metadata Indicator */}
        {node.meta && (
          <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded-full border border-yellow-200 ml-2">
            Meta
          </span>
        )}

        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
          {node.type === 'folder' ?
            `(${node.albumCount || 0} albums, ${node.imageCount || 0} imgs)` :
            ''
          }
        </span>
      </div>
      {node.children && node.children.map(child => (
        <ZipPreviewTree
          key={child.path}
          node={child}
          depth={depth + 1}
          onSelect={onSelect}
          selectedPath={selectedPath}
        />
      ))}
    </div>
  );
}
const FileListItem = ({
  file,
  getDestinationPath,
  onRemove
}: {
  file: File,
  getDestinationPath: (file: File) => Promise<string>,
  onRemove: () => void
}) => {
  const [path, setPath] = React.useState('Calculation...');

  React.useEffect(() => {
    getDestinationPath(file).then(setPath);
  }, [file, getDestinationPath]);

  return (
    <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-4 items-center p-2 rounded-lg bg-muted/50 text-sm">
      <span className="truncate" title={file.name}>{file.name}</span>
      <span className="truncate text-xs text-muted-foreground" title={path}>{path}</span>
      <span className="text-xs text-muted-foreground text-right">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 ml-2"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
};
